import { ChangeEvent, ReactNode, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "dayjs/locale/th";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  createCreditDebitNote,
  getCreditDebitNoteOriginalBill,
  getTransactionWithItems,
} from "../../services";
import { localizeApiMessage } from "../../errors/messages";
import { CreditDebitNoteOriginalBill } from "../../types/credit-debit-note.type";
import { TransactionListRow } from "../../types/sale.type";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { formatNumber } from "../../utils";
import { useBranches } from "../SpecialOrder/useBranches";
import { apiErrorMessage } from "../SpecialOrder/data";
import NoteDocumentSection from "./NoteDocumentSection";
import OriginalInvoiceSection from "./OriginalInvoiceSection";
import NoteReasonSection from "./NoteReasonSection";
import BillLinesPicker from "./BillLinesPicker";
import NoteLinesEditor from "./NoteLinesEditor";
import NoteTotalsSummary from "./NoteTotalsSummary";
import { useNoteRoutes } from "./useNoteRoutes";
import { createdNoteState } from "./createdNoteState";
import {
  CREDIT_DEBIT_NOTE_PATH,
  NOTE_REASON_LABEL,
  NOTE_REASON_MAX_LENGTH,
  NOTE_TEXT_MAX_LENGTH,
  creditDebitNoteDetailPath,
  taxTypeLabel,
} from "./constants";
import {
  BillLineDraft,
  InvoiceSource,
  NoteDraft,
  NoteLineDraft,
  buildCreateRequest,
  createInitialDraft,
  draftOriginalValue,
  draftTaxType,
  draftTotals,
  isSendableDraft,
  noteReasonType,
  validateDraft,
  withFittingEtaxCode,
} from "./noteDraft";

const summarySx = { position: { md: "sticky" }, top: { md: 16 } };

const ORIGINAL_BILL_READ_ERROR = "อ่านรายการของบิลเดิมไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/** A bill picked from the system with nothing read from it yet. */
const NO_LINKED_BILL: Pick<NoteDraft, "invoice" | "originalBill" | "billLines"> = {
  invoice: null,
  originalBill: null,
  billLines: [],
};

interface OriginalBillRead {
  bill: CreditDebitNoteOriginalBill | null;
  /** The server's refusal (a cancelled, pending-cancel or replaced bill); "" once read. */
  error: string;
}

/** The bill's lines and caps, or why it cannot carry a note; the form shows the refusal itself. */
const readOriginalBill = async (transactionUuid: string): Promise<OriginalBillRead> => {
  try {
    const {
      data: { data },
    } = await getCreditDebitNoteOriginalBill(transactionUuid);
    return { bill: data, error: data ? "" : ORIGINAL_BILL_READ_ERROR };
  } catch (error) {
    const message = apiErrorMessage(error);
    return { bill: null, error: message ? localizeApiMessage(message) : ORIGINAL_BILL_READ_ERROR };
  }
};

function Section({ title, children }: Readonly<{ title: string; children: ReactNode }>) {
  return (
    <Paper>
      <Typography variant="subtitle1" fontWeight={700} mb={2}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

/**
 * Issues a credit / debit note. The totals on screen are a preview of the server's rule; the
 * server prices the lines and stores the totals when the note is saved, then the detail page
 * shows what it stored. Errors appear once a save has been tried and follow every edit after.
 */
export default function NoteForm() {
  const navigate = useNavigate();
  const role = useUserRole();
  const canSelectBranch = featureAccess.creditDebitNoteAllBranches(role);
  const branches = useBranches(canSelectBranch);

  const [draft, setDraft] = useState<NoteDraft>(createInitialDraft);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loadingInvoice, setLoadingInvoice] = useState<boolean>(false);
  const [billLoadError, setBillLoadError] = useState<string>("");
  // Bumped by every pick, clear, source or branch change, so only the latest bill read may land.
  const latestInvoiceRequest = useRef<number>(0);

  const selectedBranch = branches.find((branch) => branch.uuid === draft.branchUuid);
  const routes = useNoteRoutes(canSelectBranch, selectedBranch?.company_id);
  const isBranchMissing = canSelectBranch && !selectedBranch;

  const errors = useMemo(
    () => (isSubmitted ? validateDraft(draft, canSelectBranch) : {}),
    [isSubmitted, draft, canSelectBranch],
  );
  const taxType = draftTaxType(draft);
  const originalValue = draftOriginalValue(draft);
  const totals = useMemo(() => draftTotals(draft), [draft]);

  /** Any edit; the e-Tax code is dropped once the note type or reason no longer offers it. */
  const handleChange = (patch: Partial<NoteDraft>) =>
    setDraft((previous) => withFittingEtaxCode({ ...previous, ...patch }));

  /** Starts the bill pick over: a read still out may no longer land. */
  const dropLinkedBill = () => {
    latestInvoiceRequest.current += 1;
    setLoadingInvoice(false);
    setBillLoadError("");
  };

  const handleChangeBranch = (branchUuid: string) => {
    dropLinkedBill();
    setDraft((previous) => ({
      ...previous,
      ...NO_LINKED_BILL,
      branchUuid,
      customer: null,
      routeCode: "",
    }));
  };

  const handleChangeSource = (invoiceSource: InvoiceSource) => {
    dropLinkedBill();
    setDraft((previous) => ({ ...previous, ...NO_LINKED_BILL, invoiceSource }));
  };

  const handleSelectInvoice = async (row: TransactionListRow | null) => {
    dropLinkedBill();
    const requestId = latestInvoiceRequest.current;
    setDraft((previous) => ({ ...previous, ...NO_LINKED_BILL }));
    setLoadingInvoice(row !== null);
    if (!row) return;
    try {
      const [
        {
          data: { data: invoice },
        },
        billRead,
      ] = await Promise.all([getTransactionWithItems(row.uuid), readOriginalBill(row.uuid)]);
      if (requestId !== latestInvoiceRequest.current) return;
      const { bill } = billRead;
      if (!invoice || !bill) {
        setBillLoadError(billRead.error || ORIGINAL_BILL_READ_ERROR);
        return;
      }
      const isKnownRoute = routes.some((route) => route.route_code === invoice.route_code);
      setDraft((previous) => ({
        ...previous,
        invoice,
        originalBill: bill,
        routeCode: isKnownRoute && invoice.route_code ? invoice.route_code : previous.routeCode,
      }));
    } catch {
      // The interceptor already shows why the bill could not be read.
    } finally {
      if (requestId === latestInvoiceRequest.current) setLoadingInvoice(false);
    }
  };

  const handleLinesChange = (lines: NoteLineDraft[]) => handleChange({ lines });
  const handleBillLinesChange = (billLines: BillLineDraft[]) => handleChange({ billLines });
  const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) =>
    handleChange({ reason: event.target.value });
  const handleRemarkChange = (event: ChangeEvent<HTMLInputElement>) =>
    handleChange({ remark: event.target.value });
  const handleBack = () => navigate(CREDIT_DEBIT_NOTE_PATH);

  const handleSave = async () => {
    setIsSubmitted(true);
    if (!isSendableDraft(draft, canSelectBranch)) return;
    setSaving(true);
    try {
      const response = await createCreditDebitNote(
        buildCreateRequest(draft, canSelectBranch ? selectedBranch?.branch_code : undefined),
      );
      const saved = response.data?.data;
      if (saved) {
        navigate(creditDebitNoteDetailPath(saved.uuid), { state: createdNoteState(saved.warnings) });
      } else {
        navigate(CREDIT_DEBIT_NOTE_PATH);
      }
    } catch {
      // The interceptor already shows the API's reason (a cap, no route stock, a closed day).
    } finally {
      setSaving(false);
    }
  };

  const reasonCounter = `${draft.reason.length}/${NOTE_REASON_MAX_LENGTH}`;
  const reasonHelperText = errors.reason ? `${errors.reason} · ${reasonCounter}` : reasonCounter;
  const priceBasisText = taxType
    ? `ราคาตามใบกำกับเดิม(${taxTypeLabel(taxType)})`
    : "ยังไม่ได้เลือกประเภทราคา";
  const isLinked = draft.invoiceSource === "LINKED";

  let linesEditor: ReactNode;
  if (!isLinked) {
    linesEditor = (
      <NoteLinesEditor lines={draft.lines} error={errors.lines} onChange={handleLinesChange} />
    );
  } else if (draft.originalBill) {
    linesEditor = (
      <BillLinesPicker
        bill={draft.originalBill}
        billLines={draft.billLines}
        noteType={draft.noteType}
        reasonType={noteReasonType(draft)}
        showLineErrors={isSubmitted}
        error={errors.lines}
        onChange={handleBillLinesChange}
      />
    );
  } else {
    linesEditor = (
      <Typography variant="body2" color="text.secondary">
        เลือกใบกำกับภาษีเดิมก่อน แล้วเลือกรายการจากบิลนั้น
      </Typography>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack gap={2}>
            <Section title="เอกสาร">
              <NoteDocumentSection
                draft={draft}
                errors={errors}
                canSelectBranch={canSelectBranch}
                branches={branches}
                routes={routes}
                onChange={handleChange}
                onChangeBranch={handleChangeBranch}
              />
            </Section>
            <Section title="ใบกำกับภาษีเดิม">
              <OriginalInvoiceSection
                draft={draft}
                errors={errors}
                originalValue={originalValue}
                branchUuid={selectedBranch?.uuid}
                companyId={selectedBranch?.company_id}
                isBranchMissing={isBranchMissing}
                loadingInvoice={loadingInvoice}
                billLoadError={billLoadError}
                onChange={handleChange}
                onChangeSource={handleChangeSource}
                onSelectInvoice={handleSelectInvoice}
              />
            </Section>
            <Section title="ประเภทเหตุผล">
              <NoteReasonSection draft={draft} errors={errors} onChange={handleChange} />
            </Section>
            <Section title="รายการสินค้า">{linesEditor}</Section>
            <Section title="สาเหตุและหมายเหตุ">
              <Stack gap={2}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  required
                  label={NOTE_REASON_LABEL[draft.noteType]}
                  value={draft.reason}
                  onChange={handleReasonChange}
                  error={Boolean(errors.reason)}
                  helperText={reasonHelperText}
                  slotProps={{ htmlInput: { maxLength: NOTE_REASON_MAX_LENGTH } }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  label="หมายเหตุ"
                  value={draft.remark}
                  onChange={handleRemarkChange}
                  slotProps={{ htmlInput: { maxLength: NOTE_TEXT_MAX_LENGTH } }}
                />
              </Stack>
            </Section>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={summarySx}>
            <Typography variant="subtitle1" fontWeight={700}>
              สรุปยอด
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {priceBasisText}: {formatNumber(totals.linesTotal)}
            </Typography>
            <NoteTotalsSummary
              originalValue={originalValue}
              correctValue={totals.correctValue}
              difference={totals.difference}
              vatAmount={totals.vatAmount}
              grandTotal={totals.grandTotal}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              ยอดนี้คำนวณเบื้องต้นบนหน้าจอ ระบบคำนวณยอดจริงอีกครั้งเมื่อบันทึก
            </Typography>
            <Stack gap={1} mt={2}>
              <Button
                variant="contained"
                loading={saving}
                disabled={loadingInvoice}
                onClick={handleSave}
              >
                ออกเอกสาร
              </Button>
              <Button variant="text" disabled={saving} onClick={handleBack}>
                ยกเลิก
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}
